// Phase 7 R3 — list receipts (with nested lines) for a PO.
// Phase 7 R5 — also resolves the receiver's display name (read-only) so the
// history card header can show "{date} · {actor} · {ref} · {n} line(s)".
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PartsReceiptLine {
  id: string;
  parts_receipt_id: string;
  purchase_order_line_id: string;
  shop_id: string;
  quantity_received: number;
  unit_cost_actual: number | null;
  core_charge_actual: number | null;
  notes: string | null;
  created_at: string;
}

export interface PartsReceipt {
  id: string;
  shop_id: string;
  purchase_order_id: string;
  received_by: string | null;
  received_by_name: string | null;
  received_at: string;
  packing_slip_ref: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  parts_receipt_lines: PartsReceiptLine[];
}

export function usePurchaseOrderReceipts(poId: string | undefined) {
  return useQuery({
    queryKey: ['po-receipts', poId],
    enabled: !!poId,
    queryFn: async (): Promise<PartsReceipt[]> => {
      const { data, error } = await (supabase as any)
        .from('parts_receipts')
        .select('*, parts_receipt_lines(*)')
        .eq('purchase_order_id', poId)
        .order('received_at', { ascending: false });
      if (error) throw error;

      const rows = (data ?? []) as Array<Omit<PartsReceipt, 'received_by_name'>>;
      const ids = Array.from(new Set(rows.map((r) => r.received_by).filter((x): x is string => !!x)));

      let nameById = new Map<string, string>();
      if (ids.length > 0) {
        const { data: profiles } = await (supabase as any)
          .from('profiles')
          .select('id, full_name, email')
          .in('id', ids);
        nameById = new Map(
          (profiles ?? []).map((p: any) => [
            p.id as string,
            (p.full_name as string | null) || (p.email as string | null) || 'Unknown user',
          ]),
        );
      }

      return rows.map((r) => ({
        ...r,
        received_by_name: r.received_by ? nameById.get(r.received_by) ?? 'Unknown user' : null,
      }));
    },
  });
}
